// One connection (institution) group on `#s01`: a header with the
// institution name, account count, and `ConnectionStatusPill`, then its
// account rows. Manual accounts render under a `Manual` pseudo-connection
// and are excluded from sync health (execution plan §4 rule 5). The design
// groups by connection, not by account class -- the class label lives in
// each row instead (§9 conflict #11).
import { useState } from 'react';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@moneyskyline/ui';
import { toast } from 'sonner';
import { accountRemovalErrorMessage, useRemoveItemAccounts } from '@/api/hooks/useItemMutations';
import type { Account, CanonicalAccount, ConnectionStatus } from '@/api/hooks/useAccounts';
import { ConnectionStatusPill } from '@/components/ConnectionStatusPill';
import { AmountText } from '@/components/AmountText';
import { AccountRow } from './AccountRow';
import { computeNetWorthRollup } from './netWorthRollup';

export interface ConnectionGroupData {
  key: string;
  institutionName: string;
  status: ConnectionStatus;
  lastSyncedAt: string | null;
  accounts: Account[];
}

export type CanonicalConnectionGroupsResult =
  | { state: 'ready'; groups: ConnectionGroupData[] }
  | { state: 'inconsistent'; groups: [] };

export function groupAccountsByConnection(accounts: Account[]): ConnectionGroupData[] {
  const groups = new Map<string, ConnectionGroupData>();
  for (const account of accounts) {
    const isManual = account.source === 'manual';
    const key = isManual ? 'manual' : (account.item_id ?? account.institution_id ?? account.institution_name ?? 'unknown');
    const existing = groups.get(key);
    if (existing) {
      existing.accounts.push(account);
      // A connection is only as healthy as its least-healthy account.
      if (account.status !== 'synced' && account.status !== 'manual') existing.status = account.status;
      if (account.last_synced_at && (!existing.lastSyncedAt || account.last_synced_at > existing.lastSyncedAt)) {
        existing.lastSyncedAt = account.last_synced_at;
      }
      continue;
    }
    groups.set(key, {
      key,
      institutionName: isManual ? 'Manual' : (account.institution_name ?? 'Unknown institution'),
      status: isManual ? 'manual' : account.status,
      lastSyncedAt: account.last_synced_at,
      accounts: [account],
    });
  }
  return Array.from(groups.values());
}

export function groupCanonicalAccountsForPresentation(accounts: readonly CanonicalAccount[]): CanonicalConnectionGroupsResult {
  const seen = new Set<string>();
  for (const account of accounts) {
    if (account.canonical_account_id === null) continue;
    if (seen.has(account.canonical_account_id)) return { state: 'inconsistent', groups: [] };
    seen.add(account.canonical_account_id);
  }
  return { state: 'ready', groups: groupAccountsByConnection(Array.from(accounts)) };
}

export function ConnectionGroup({
  group,
  dense = false,
  className,
  aggregateDisplay = 'physical',
}: {
  group: ConnectionGroupData;
  dense?: boolean;
  className?: string;
  aggregateDisplay?: 'physical' | 'canonical_unavailable';
}) {
  // A per-connection subtotal, same rules as the page-level rollup: never a
  // fabricated number when every account in the group is unknown, and a
  // visibly-partial figure (never silently summed as complete) otherwise.
  const subtotal = aggregateDisplay === 'physical' ? computeNetWorthRollup(group.accounts) : null;
  const canRemoveAll = group.status === 'error' && group.key !== 'manual' && group.accounts.length > 0 && group.accounts.every((account) => account.status_detail === 'connection_disconnected');

  return (
    <section className={className} data-testid="connection-group" data-connection-key={group.key}>
      <div className="flex items-center justify-between px-1 py-1.5 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">
          {group.institutionName} <span className="font-normal text-muted-foreground">· {group.accounts.length} account{group.accounts.length === 1 ? '' : 's'}</span>
        </span>
        <div className="flex items-center gap-2">
          {group.status !== 'manual' && (
            <ConnectionStatusPill status={group.status} lastSyncedAt={group.lastSyncedAt} errorCode={group.accounts.find((account) => account.status === group.status)?.status_detail} />
          )}
          {canRemoveAll && <RemoveAllAccountsControl group={group} />}
        </div>
      </div>
      <div className="rounded-lg border border-border bg-card divide-y divide-border-strong/40">
        {group.accounts.map((account) => (
          <AccountRow key={account.account_id} account={account} dense={dense} />
        ))}
        {group.accounts.length > 1 && (
          <div
            className="flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-muted-foreground"
            data-testid="connection-group-subtotal"
          >
            <span>Subtotal</span>
            {aggregateDisplay === 'canonical_unavailable' ? (
              <span className="text-degraded-foreground">unavailable</span>
            ) : subtotal?.total === null ? (
              <span className="text-degraded-foreground">no balance yet</span>
            ) : (
              <AmountText value={String(subtotal?.total)} variant="balance" balanceStatus={subtotal?.shape === 'partial' ? 'stale' : 'ok'} />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function RemoveAllAccountsControl({ group }: { group: ConnectionGroupData }) {
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const removeItemAccounts = useRemoveItemAccounts();
  return (
    <>
      <Button type="button" variant="outline" onClick={() => { setRemoveError(null); setRemoveOpen(true); }}>Remove all accounts</Button>
      <Dialog open={removeOpen} onOpenChange={(open) => { setRemoveOpen(open); if (!open) setRemoveError(null); }}>
        <DialogContent data-testid="remove-all-accounts-dialog">
          <DialogHeader>
            <DialogTitle>Remove all accounts from this disconnected connection?</DialogTitle>
            <DialogDescription>This permanently hides the following accounts: {group.accounts.map((account) => account.display_name).join(', ')} from Accounts, Transactions, net worth, and Business. Their records stay for audit purposes and cannot be restored here.</DialogDescription>
          </DialogHeader>
          {removeError && <p role="alert" className="text-sm text-destructive">{removeError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRemoveOpen(false)} disabled={removeItemAccounts.isPending}>Cancel</Button>
            <Button type="button" variant="destructive" disabled={removeItemAccounts.isPending} onClick={() => removeItemAccounts.mutate(group.key, { onError: (error) => setRemoveError(accountRemovalErrorMessage(error)), onSuccess: (result) => { setRemoveOpen(false); const message = result.removed_account_ids.length > 0 ? 'Accounts removed.' : result.already_removed_account_ids.length > 0 ? 'Accounts were already hidden.' : 'No accounts were changed.'; toast.success(message); } })}>{removeItemAccounts.isPending ? 'Removing…' : 'Remove all accounts'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ConnectionGroup;
